
import { defineComponent, PropType, computed, ref } from "vue";
import { GroupedAlert } from "@/stores/telegramAlerts";
import MediaDisplay from "@/components/alerts/MediaDisplay.vue";
import AlertEditForm, {
  AlertEditData,
} from "@/components/alerts/AlertEditForm.vue";
import {
  formatDateTime,
  cleanAlertText,
} from "@/components/alerts/utils/alertFormatters";
import {
  getConfidenceBadgeClass,
  getMessageTypeLabel,
  getMessageTypeIcon,
} from "@/components/alerts/utils/alertStatus";
import {
  getOrderTypeLabel,
  getOrderTypeBadgeClass,
} from "@/components/alerts/utils/orderTypeHelpers";
import { MediaType } from "@/stores/telegramAlerts";
import { useAlertPermissions } from "@/composables/useAlertPermissions";

export default defineComponent({
  name: "AlertDetails",
  components: {
    MediaDisplay,
    AlertEditForm,
  },
  props: {
    alert: {
      type: Object as PropType<GroupedAlert>,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    isReprocessing: {
      type: Object as PropType<Record<number, boolean>>,
      default: () => ({}),
    },
    currentAlertType: {
      type: String,
      default: "sports_betting",
    },
  },
  emits: ["start-edit", "cancel-edit", "save-edit", "reprocess-message"],
  setup(props, { emit }) {
    const showReprocessButtons = ref(false);
    const { canEditAlerts, canReanalyzeAlerts } = useAlertPermissions();

    // Helper to filter out media-only messages linked to main alert (displayed inline)
    const getMediaOnlyLinkedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.filter((msg) => {
        const hasMedia = msg.mediaType && msg.mediaType !== MediaType.NONE;
        const hasNoText = !msg.text || msg.text.trim() === "";
        return hasMedia && hasNoText;
      });
    };

    // Helper to get remaining related messages (textual updates/results)
    const getTextRelatedMessages = (alert: GroupedAlert) => {
      return alert.relatedMessages.filter((msg) => {
        const hasMedia = msg.mediaType && msg.mediaType !== MediaType.NONE;
        const hasNoText = !msg.text || msg.text.trim() === "";
        return !(hasMedia && hasNoText);
      });
    };

    // Prepare data for EditForm
    const alertEditData = computed<AlertEditData>(() => {
      const a = props.alert;
      return {
        stake: a.stake,
        odds: a.odds,
        resultStatus: a.resultStatus,
        pair: a.mainMessage.pair,
        index: a.mainMessage.index,
        direction: a.mainMessage.direction,
        orderType: a.mainMessage.orderType,
        entryPrice: a.mainMessage.entryPrice,
        stopLoss: a.mainMessage.stopLoss,
        takeProfit: a.mainMessage.takeProfit,
        exitPrice: a.mainMessage.exitPrice,
      };
    });

    const onSaveEdit = (data: AlertEditData) => {
      emit("save-edit", { alert: props.alert, data });
    };

    return {
      formatDateTime,
      cleanAlertText,
      getConfidenceBadgeClass,
      getMessageTypeLabel,
      getMessageTypeIcon,
      getMediaOnlyLinkedMessages,
      getTextRelatedMessages,
      alertEditData,
      onSaveEdit,
      getOrderTypeLabel,
      getOrderTypeBadgeClass,
      showReprocessButtons,
      canEditAlerts,
      canReanalyzeAlerts,
    };
  },
});
